export const SPEAKING_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: { ideal: 1 },
};

export const NOISE_MONITOR = {
  HIGH_THRESHOLD: 0.08,
  SUSTAINED_MS: 2500,
  POLL_INTERVAL_MS: 400,
};

export const buildAudioConstraints = (deviceId) => ({
  audio: {
    ...SPEAKING_AUDIO_CONSTRAINTS,
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  },
});

export const requestSpeakingMicrophone = async (deviceId) => {
  const supported = navigator.mediaDevices.getSupportedConstraints?.() || {};
  const audio = { ...SPEAKING_AUDIO_CONSTRAINTS };

  if (supported.voiceIsolation) {
    audio.voiceIsolation = true;
  }

  if (deviceId) {
    audio.deviceId = { exact: deviceId };
  }

  return navigator.mediaDevices.getUserMedia({ audio });
};

export const enumerateAudioInputDevices = async () => {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((device) => device.kind === 'audioinput');
};

export const measureAudioLevel = (analyser, dataArray) => {
  analyser.getByteTimeDomainData(dataArray);
  let sum = 0;

  for (let i = 0; i < dataArray.length; i += 1) {
    const normalized = (dataArray[i] - 128) / 128;
    sum += normalized * normalized;
  }

  return Math.sqrt(sum / dataArray.length);
};

export const analyzeBackgroundNoise = async (
  stream,
  sampleDurationMs = 1500
) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);

  const dataArray = new Uint8Array(analyser.fftSize);
  const samples = [];
  const intervalMs = 100;
  const maxSamples = Math.ceil(sampleDurationMs / intervalMs);

  await new Promise((resolve) => {
    let count = 0;
    const timer = setInterval(() => {
      samples.push(measureAudioLevel(analyser, dataArray));
      count += 1;
      if (count >= maxSamples) {
        clearInterval(timer);
        resolve();
      }
    }, intervalMs);
  });

  source.disconnect();
  await audioContext.close();

  const averageLevel =
    samples.reduce((total, value) => total + value, 0) / samples.length;
  const peakLevel = Math.max(...samples);
  const isNoisy = averageLevel > 0.035 || peakLevel > 0.12;
  const isModerate = averageLevel > 0.02 || peakLevel > 0.08;

  return {
    averageLevel,
    peakLevel,
    isNoisy,
    isModerate,
    level: isNoisy ? 'high' : isModerate ? 'moderate' : 'low',
  };
};

export const pickPreferredInputDevice = (devices = []) => {
  if (!devices.length) {
    return null;
  }

  return (
    devices.find((device) =>
      /headset|headphone|airpod|earbud|bluetooth/i.test(device.label)
    ) || devices[0]
  );
};

export const runQuickSessionSetupCheck = async (deviceId = '') => {
  const issues = [];
  const recommendations = [];
  let resolvedDeviceId = deviceId;
  let stream;

  try {
    stream = await requestSpeakingMicrophone(deviceId || undefined);
  } catch (error) {
    return {
      ok: false,
      micGranted: false,
      noiseLevel: 'unknown',
      deviceId: resolvedDeviceId,
      issues: ['Microphone access was denied or unavailable.'],
      recommendations: [
        'Allow microphone access in your browser settings, then try again.',
      ],
      error,
    };
  }

  try {
    const devices = await enumerateAudioInputDevices();
    const activeTrack = stream.getAudioTracks()[0];
    const settings = activeTrack?.getSettings?.() || {};

    if (settings.deviceId) {
      resolvedDeviceId = settings.deviceId;
    } else if (!resolvedDeviceId && devices.length > 0) {
      resolvedDeviceId = pickPreferredInputDevice(devices)?.deviceId || '';
    }

    const noiseAnalysis = await analyzeBackgroundNoise(stream, 1200);

    if (noiseAnalysis.level === 'high') {
      issues.push('High background noise detected around you.');
      recommendations.push(
        'Enable push-to-talk, use a headset, or move to a quieter room.'
      );
    } else if (noiseAnalysis.level === 'moderate') {
      issues.push('Some background noise detected.');
      recommendations.push(
        'A headset microphone or push-to-talk will improve accuracy.'
      );
    }

    return {
      ok: true,
      micGranted: true,
      noiseLevel: noiseAnalysis.level,
      noiseAnalysis,
      deviceId: resolvedDeviceId,
      issues,
      recommendations,
    };
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
};

export const getActiveAudioProtectionFeatures = () => {
  const supported = navigator.mediaDevices?.getSupportedConstraints?.() || {};
  const features = [
    'Noise suppression',
    'Echo cancellation',
    'Auto gain control',
  ];

  if (supported.voiceIsolation) {
    features.push('Voice isolation');
  }

  return features;
};

export const getNoiseGuidance = (level) => {
  if (level === 'high') {
    return {
      title: 'High background noise detected',
      message:
        'Other voices or loud sounds may interrupt your session. Use a headset, move to a quieter room, or mute the mic when you are not speaking.',
    };
  }

  if (level === 'moderate') {
    return {
      title: 'Some background noise detected',
      message:
        'Your environment is usable, but a quieter space or headset microphone will improve accuracy.',
    };
  }

  return null;
};
